"use client"

import * as React from 'react'
import { z } from 'zod'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@open-mercato/ui/primitives/dialog'
import { Button } from '@open-mercato/ui/primitives/button'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { createCrudFormError } from '@open-mercato/ui/backend/utils/serverErrors'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { DRIVER_QUALIFICATIONS, LICENSE_CATEGORIES } from './lookups'
import type { DriverDetail, DriverTranslate } from './driversTable'

const isoDate = z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional()
const optText = z.string().trim().nullable().optional()

const driverDialogSchema = z
  .object({
    firstName: z.string().trim().min(1, 'drivers.validation.firstNameRequired'),
    lastName: z.string().trim().min(1, 'drivers.validation.lastNameRequired'),
    phone: optText,
    email: z.string().trim().email().nullable().optional(),
    pesel: z.string().trim().length(11).nullable().optional(),
    licenseCategories: z.array(z.enum(LICENSE_CATEGORIES)).default([]),
    licenseNumber: optText,
    licenseIssuedBy: optText,
    licenseExpires: isoDate,
    qualifications: z.array(z.enum(DRIVER_QUALIFICATIONS)).default([]),
    adrCertificateNumber: optText,
    adrExpires: isoDate,
    digitalTachographId: optText,
    driverCardExpires: isoDate,
    cpcNumber: optText,
    cpcExpires: isoDate,
    userId: z.string().uuid().nullable().optional(),
    isActive: z.boolean().optional().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.adrExpires && !data.adrCertificateNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['adrCertificateNumber'],
        message: 'drivers.validation.adrCertificateNumberRequired',
      })
    }
  })

export type DriverFormValues = z.infer<typeof driverDialogSchema>

export type DriverMutationPayload = {
  firstName: string
  lastName: string
  phone?: string | null
  email?: string | null
  pesel?: string | null
  licenseCategories?: string[]
  licenseNumber?: string | null
  licenseIssuedBy?: string | null
  licenseExpires?: string | null
  qualifications?: string[]
  adrCertificateNumber?: string | null
  adrExpires?: string | null
  digitalTachographId?: string | null
  driverCardExpires?: string | null
  cpcNumber?: string | null
  cpcExpires?: string | null
  userId?: string | null
  isActive?: boolean
}

function n(v: unknown): string | null | undefined {
  if (v == null) return undefined
  const s = typeof v === 'string' ? v.trim() : null
  return s && s.length > 0 ? s : null
}

export function toDriverMutationPayload(values: DriverFormValues): DriverMutationPayload {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    phone: n(values.phone),
    email: n(values.email),
    pesel: n(values.pesel),
    licenseCategories: values.licenseCategories,
    licenseNumber: n(values.licenseNumber),
    licenseIssuedBy: n(values.licenseIssuedBy),
    licenseExpires: n(values.licenseExpires),
    qualifications: values.qualifications,
    adrCertificateNumber: n(values.adrCertificateNumber),
    adrExpires: n(values.adrExpires),
    digitalTachographId: n(values.digitalTachographId),
    driverCardExpires: n(values.driverCardExpires),
    cpcNumber: n(values.cpcNumber),
    cpcExpires: n(values.cpcExpires),
    userId: n(values.userId),
    isActive: values.isActive ?? true,
  }
}

export function toDriverFormValues(driver?: Partial<DriverDetail> | null): DriverFormValues {
  return {
    firstName: driver?.firstName ?? '',
    lastName: driver?.lastName ?? '',
    phone: driver?.phone ?? null,
    email: driver?.email ?? null,
    pesel: driver?.pesel ?? null,
    licenseCategories: (driver?.licenseCategories ?? []) as DriverFormValues['licenseCategories'],
    licenseNumber: driver?.licenseNumber ?? null,
    licenseIssuedBy: driver?.licenseIssuedBy ?? null,
    licenseExpires: driver?.licenseExpires ?? null,
    qualifications: (driver?.qualifications ?? []) as DriverFormValues['qualifications'],
    adrCertificateNumber: driver?.adrCertificateNumber ?? null,
    adrExpires: driver?.adrExpires ?? null,
    digitalTachographId: driver?.digitalTachographId ?? null,
    driverCardExpires: driver?.driverCardExpires ?? null,
    cpcNumber: driver?.cpcNumber ?? null,
    cpcExpires: driver?.cpcExpires ?? null,
    userId: driver?.userId ?? null,
    isActive: driver?.isActive ?? true,
  }
}

type DriverPwaUserOptionsResponse = {
  items: Array<{
    id: string
    email: string
  }>
}

async function loadDriverPwaUserOptions(query?: string) {
  const search = query?.trim()
  const params = new URLSearchParams()
  if (search) params.set('q', search)
  const response = await readApiResultOrThrow<DriverPwaUserOptionsResponse>(
    `/api/road/drivers/pwa-users${params.toString() ? `?${params.toString()}` : ''}`,
    undefined,
    { errorMessage: 'Failed to load driver PWA users.' },
  )
  return response.items.map((item) => ({
    value: item.id,
    label: item.email,
  }))
}

export function buildDriverFormFields(t: DriverTranslate): CrudField[] {
  return [
    { id: 'firstName', label: t('drivers.detail.fields.firstName', 'First name'), type: 'text', required: true, layout: 'half' },
    { id: 'lastName', label: t('drivers.detail.fields.lastName', 'Last name'), type: 'text', required: true, layout: 'half' },
    { id: 'phone', label: t('drivers.detail.fields.phone', 'Phone'), type: 'text', layout: 'half' },
    { id: 'email', label: t('drivers.detail.fields.email', 'Email'), type: 'text', layout: 'half' },
    { id: 'pesel', label: t('drivers.detail.profile.peselLabel', 'PESEL'), type: 'text', layout: 'half' },
    { id: 'isActive', label: t('drivers.table.columns.isActive', 'Active'), type: 'checkbox', layout: 'half' },
    {
      id: 'licenseCategories',
      label: t('drivers.detail.fields.licenseCategories', 'License categories'),
      type: 'tags',
      suggestions: [...LICENSE_CATEGORIES],
      allowCustomValues: false,
      layout: 'full',
    },
    { id: 'licenseNumber', label: t('drivers.detail.fields.licenseNumber', 'License number'), type: 'text', layout: 'half' },
    { id: 'licenseIssuedBy', label: t('drivers.detail.fields.licenseIssuedBy', 'Issued by'), type: 'text', layout: 'half' },
    { id: 'licenseExpires', label: t('drivers.detail.fields.licenseExpires', 'Expires'), type: 'date', layout: 'half' },
    {
      id: 'qualifications',
      label: t('drivers.table.columns.qualifications', 'Qualifications'),
      type: 'tags',
      suggestions: [...DRIVER_QUALIFICATIONS],
      allowCustomValues: false,
      layout: 'full',
    },
    { id: 'adrCertificateNumber', label: t('drivers.detail.qualifications.certificateNumber', 'ADR certificate number'), type: 'text', layout: 'half' },
    { id: 'adrExpires', label: t('drivers.table.columns.adrExpires', 'ADR exp.'), type: 'date', layout: 'half' },
    { id: 'digitalTachographId', label: t('drivers.detail.qualifications.cardNumber', 'Tachograph card number'), type: 'text', layout: 'half' },
    { id: 'driverCardExpires', label: t('drivers.table.columns.driverCardExpires', 'Card exp.'), type: 'date', layout: 'half' },
    { id: 'cpcNumber', label: t('drivers.detail.qualifications.certificateNumber', 'CPC certificate number'), type: 'text', layout: 'half' },
    { id: 'cpcExpires', label: t('drivers.table.columns.cpcExpires', 'CPC exp.'), type: 'date', layout: 'half' },
    {
      id: 'userId',
      label: t('drivers.detail.pwa.linkedUserTitle', 'Linked OM User'),
      type: 'combobox',
      layout: 'full',
      loadOptions: loadDriverPwaUserOptions,
      allowCustomValues: false,
      placeholder: t('drivers.detail.pwa.linkedUserPlaceholder', 'Search organization users…'),
    },
  ]
}

export function buildDriverFormGroups(t: DriverTranslate): CrudFormGroup[] {
  return [
    {
      id: 'personal',
      title: t('drivers.detail.profile.personalTitle', 'Personal info'),
      fields: ['firstName', 'lastName', 'phone', 'email', 'pesel', 'isActive'],
    },
    {
      id: 'license',
      title: t('drivers.detail.profile.licenseTitle', 'Driving license'),
      fields: ['licenseCategories', 'licenseNumber', 'licenseIssuedBy', 'licenseExpires'],
    },
    {
      id: 'qualifications',
      title: t('drivers.detail.tabs.qualifications', 'Qualifications'),
      fields: ['qualifications', 'adrCertificateNumber', 'adrExpires'],
    },
    {
      id: 'tachograph',
      title: t('drivers.detail.qualifications.tachographTitle', 'Tachograph & CPC'),
      fields: ['digitalTachographId', 'driverCardExpires', 'cpcNumber', 'cpcExpires'],
    },
    {
      id: 'pwa',
      title: t('drivers.detail.tabs.pwaAccess', 'PWA Access'),
      fields: ['userId'],
    },
  ]
}

export function assertDriverDialogPayload(
  t: DriverTranslate,
  values: DriverFormValues,
): DriverMutationPayload {
  const parsed = driverDialogSchema.safeParse(values)
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {}
    for (const issue of parsed.error.issues) {
      const fieldId = typeof issue.path[0] === 'string' ? issue.path[0] : 'form'
      fieldErrors[fieldId] = t(issue.message, issue.message)
    }
    throw createCrudFormError(
      t('drivers.messages.updateError', 'Please fix the highlighted fields.'),
      fieldErrors,
    )
  }
  return toDriverMutationPayload(parsed.data)
}

export function DriverDialog({
  open,
  onOpenChange,
  mode,
  initialValues,
  onSubmit,
  loading = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  initialValues?: DriverFormValues
  onSubmit: (payload: DriverMutationPayload) => Promise<void>
  loading?: boolean
}) {
  const t = useT()
  const fields = React.useMemo(() => buildDriverFormFields(t), [t])
  const groups = React.useMemo(() => buildDriverFormGroups(t), [t])
  const title = mode === 'create'
    ? t('drivers.actions.addDriver', 'Add driver')
    : t('drivers.actions.editInline', 'Edit driver')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <CrudForm<DriverFormValues>
          key={`${mode}:${initialValues?.lastName ?? 'new'}:${open ? 'open' : 'closed'}`}
          schema={driverDialogSchema}
          embedded={true}
          isLoading={loading}
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          submitLabel={mode === 'create'
            ? t('drivers.actions.save', 'Create driver')
            : t('drivers.actions.save', 'Save changes')}
          extraActions={(
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
          )}
          onSubmit={async (values) => {
            await onSubmit(assertDriverDialogPayload(t, values))
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
